import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { COMPANIES } from '../constants';
import history from '../history';

export const initialValues = {
  isYachtsAndFriends: false
};

export const ActiveCompanyContext = createContext(initialValues);

export const ActiveCompanyProvider = ({ children }) => {
  const currentBooking = useSelector(state => state.booking.currentBooking);
  const [currentPath, setCurrentPath] = useState(history.location.pathname);
  const [currentSearch, setCurrentSearch] = useState(history.location.search);

  // Listen to route changes
  useEffect(() => {
    const unlisten = history.listen(location => {
      setCurrentPath(location.pathname);
      setCurrentSearch(location.search);
    });

    return () => {
      unlisten();
    };
  }, []);

  // Determine isYachtsAndFriends based on route, query params and booking
  const isYachtsAndFriends = useMemo(() => {
    // Query param override (e.g. account/login?brand=ynf links in YNF emails)
    if (new URLSearchParams(currentSearch).get('brand') === 'ynf') {
      return true;
    }

    // Route-based detection
    if (currentPath.includes('/yachtsandfriends')) {
      return true;
    }
    if (currentPath === '/browse' || currentPath.startsWith('/browse/')) {
      return false;
    }

    // Booking-based detection for /account/booking/:id pages
    if (currentPath.match(/^\/account\/booking\/\d+/)) {
      if (currentBooking?.week?.company?.id === COMPANIES.YNF) {
        return true;
      }
      if (currentBooking?.week?.company?.id === COMPANIES.DAY8) {
        return false;
      }
      // If booking is not loaded yet, default to false
      return false;
    }

    // Default to The Yacht Week (false) for all other routes
    return false;
  }, [currentPath, currentSearch, currentBooking]);

  const contextValue = useMemo(() => ({ isYachtsAndFriends }), [isYachtsAndFriends]);

  return (
    <ActiveCompanyContext.Provider value={contextValue}>
      {children}
    </ActiveCompanyContext.Provider>
  );
};

ActiveCompanyProvider.propTypes = {
  children: PropTypes.any
};

export const useActiveCompany = () => useContext(ActiveCompanyContext);

export const withActiveCompany = Component => {
  const WrappedComponent = props => {
    const { isYachtsAndFriends } = useActiveCompany();
    return <Component {...props} isYachtsAndFriends={isYachtsAndFriends} />;
  };
  WrappedComponent.displayName = `WithActiveCompany(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
};